import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import { Search, Plus, Pencil, Trash2, Eye, ChevronLeft, ChevronRight } from 'lucide-react';
import { PLAN_COLORS } from './AdminDashboard';
import { CreateUserDialog } from './CreateDialog';
import { EditUserDialog } from './EditDialog';
import { DeleteUserDialog } from './DeleteDialog';
import { DetailUserDialog } from './DetailDialog';

const LIMIT = 15;

export const AdminUsers = ({ api, users, setUsers, totalUsers, page, setPage, searchTerm, setSearchTerm, planFilter, setPlanFilter, statusFilter, setStatusFilter, fetchUsers, fetchStats }) => {
  const [createOpen, setCreateOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [deleteUser, setDeleteUser] = useState(null);
  const [detailUser, setDetailUser] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', password: '', plan: 'STARTER' });
  const [editForm, setEditForm] = useState({ name: '', email: '', plan: '', isActive: true });
  const [submitting, setSubmitting] = useState(false);
  const totalPages = Math.ceil(totalUsers / LIMIT);

  const handleCreate = async () => {
    if (!form.name || !form.email || !form.password) { toast.error('Remplissez tous les champs'); return; }
    setSubmitting(true);
    try {
      await api.post('/admin/users', form);
      toast.success("Compte cr\u00e9\u00e9");
      setCreateOpen(false);
      setForm({ name: '', email: '', password: '', plan: 'STARTER' });
      fetchUsers(); fetchStats();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
    finally { setSubmitting(false); }
  };

  const handleEdit = async () => {
    if (!editUser) return;
    setSubmitting(true);
    try {
      const res = await api.put(`/admin/users/${editUser.id}`, editForm);
      toast.success("Mis \u00e0 jour");
      setEditUser(null);
      setUsers(prev => prev.map(u => u.id === editUser.id ? { ...u, ...res.data } : u));
      fetchStats();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
    finally { setSubmitting(false); }
  };

  const handleDelete = async () => {
    if (!deleteUser) return;
    try {
      await api.delete(`/admin/users/${deleteUser.id}`);
      toast.success("Supprim\u00e9");
      setDeleteUser(null); fetchUsers(); fetchStats();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  const openEdit = (u) => {
    setEditForm({ name: u.name, email: u.email, plan: u.plan, isActive: u.isActive !== false });
    setEditUser(u);
  };

  const openDetail = async (u) => {
    try { const res = await api.get(`/admin/users/${u.id}`); setDetailUser(res.data); }
    catch { toast.error("Erreur"); }
  };

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <CardTitle>Utilisateurs ({totalUsers})</CardTitle>
            <Button onClick={() => setCreateOpen(true)} className="rounded-full" data-testid="create-user-btn">
              <Plus className="w-4 h-4 mr-1.5" /> {"Cr\u00e9er un compte"}
            </Button>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 pt-4">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input placeholder="Rechercher..." value={searchTerm} onChange={(e) => { setSearchTerm(e.target.value); setPage(0); }} className="pl-10" data-testid="admin-search-input" />
            </div>
            <Select value={planFilter} onValueChange={(v) => { setPlanFilter(v); setPage(0); }}>
              <SelectTrigger className="w-36" data-testid="plan-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">Tous</SelectItem>
                <SelectItem value="STARTER">STARTER</SelectItem>
                <SelectItem value="PRO">PRO</SelectItem>
                <SelectItem value="PREMIUM">PREMIUM</SelectItem>
              </SelectContent>
            </Select>
            <Select value={statusFilter || 'all'} onValueChange={(v) => { setStatusFilter(v === 'all' ? '' : v); setPage(0); }}>
              <SelectTrigger className="w-32" data-testid="status-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous</SelectItem>
                <SelectItem value="active">Actifs</SelectItem>
                <SelectItem value="inactive">Inactifs</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          <UserTable users={users} openDetail={openDetail} openEdit={openEdit} setDeleteUser={setDeleteUser} />
          {totalPages > 1 && (
            <div className="flex items-center justify-between pt-4" data-testid="pagination">
              <p className="text-sm text-muted-foreground">Page {page + 1} / {totalPages}</p>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(p => p - 1)}><ChevronLeft className="w-4 h-4" /></Button>
                <Button variant="outline" size="sm" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}><ChevronRight className="w-4 h-4" /></Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
      <CreateUserDialog open={createOpen} onClose={() => setCreateOpen(false)} form={form} setForm={setForm} onSubmit={handleCreate} submitting={submitting} />
      <EditUserDialog user={editUser} onClose={() => setEditUser(null)} form={editForm} setForm={setEditForm} onSubmit={handleEdit} submitting={submitting} />
      <DeleteUserDialog user={deleteUser} onClose={() => setDeleteUser(null)} onConfirm={handleDelete} />
      <DetailUserDialog user={detailUser} onClose={() => setDetailUser(null)} />
    </>
  );
};

const UserTable = ({ users, openDetail, openEdit, setDeleteUser }) => (
  <div className="overflow-x-auto">
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Utilisateur</TableHead>
          <TableHead>Plan</TableHead>
          <TableHead>Statut</TableHead>
          <TableHead>Boutiques</TableHead>
          <TableHead>Produits</TableHead>
          <TableHead>Inscription</TableHead>
          <TableHead className="text-right">Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {users.map((u) => (
          <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
            <TableCell>
              <p className="font-medium text-sm">{u.name}</p>
              <p className="text-xs text-muted-foreground">{u.email}</p>
            </TableCell>
            <TableCell><Badge className={PLAN_COLORS[u.plan]}>{u.plan}</Badge></TableCell>
            <TableCell>
              <Badge className={u.isActive !== false ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300' : 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'}>
                {u.isActive !== false ? 'Actif' : 'Inactif'}
              </Badge>
            </TableCell>
            <TableCell className="text-sm">{u.shopCount || 0}</TableCell>
            <TableCell className="text-sm">{u.productCount || 0}</TableCell>
            <TableCell className="text-sm text-muted-foreground">{new Date(u.createdAt).toLocaleDateString('fr-FR')}</TableCell>
            <TableCell className="text-right">
              <div className="flex justify-end gap-1">
                <Button variant="ghost" size="icon" onClick={() => openDetail(u)} data-testid={`view-user-${u.id}`}><Eye className="w-4 h-4" /></Button>
                <Button variant="ghost" size="icon" onClick={() => openEdit(u)} data-testid={`edit-user-${u.id}`}><Pencil className="w-4 h-4" /></Button>
                <Button variant="ghost" size="icon" onClick={() => setDeleteUser(u)} className="text-destructive hover:text-destructive" data-testid={`delete-user-${u.id}`}><Trash2 className="w-4 h-4" /></Button>
              </div>
            </TableCell>
          </TableRow>
        ))}
        {users.length === 0 && (
          <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">Aucun utilisateur</TableCell></TableRow>
        )}
      </TableBody>
    </Table>
  </div>
);

export default AdminUsers;
